import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';

// ui components
import { makeStyles } from "@material-ui/core/styles";
import TextField from '@material-ui/core/TextField';
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';

// core components
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Info from "beaktShared/components/Typography/Info.js";
import Warning from "beaktShared/components/Typography/Warning.js";
import Muted from "beaktShared/components/Typography/Muted.js";

// services
import { MaskDate, formateDate, validateDate, convertDate } from "beaktShared/helpers/masks";
import { TIPO_CONTRATO, getSugestaoDatasContrato, separarContratos, renovacaoVigente, cicloVencido } from "./ContratoEstabelecimentoService";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
const useStyles = makeStyles(styles);

// Formulário do Contrato Geral (vazio = sem Geral informado na tela)
export const CONTRATO_GERAL_FORM_VAZIO = {
  inicio: "",
  termino: "",
  terminoSugerido: false,
  terminoEditado: false,
  // BKT-1514: início alterado e nova sugestão de término ainda não aplicada (bloqueia o salvar)
  sugestaoPendente: false,
};

export const contratoGeralParaForm = (geral) => geral
  ? { ...CONTRATO_GERAL_FORM_VAZIO, inicio: formateDate(geral.inicioContrato), termino: formateDate(geral.terminoContrato) }
  : CONTRATO_GERAL_FORM_VAZIO;

const DEBOUNCE_SUGESTAO_MS = 400;

// BKT-1508: resumo somente leitura do ciclo de Renovação vigente e do índice aplicado
export function RenovacaoVigenteResumo({ contratos }) {
  const { t } = useTranslation();
  const vigente = renovacaoVigente(separarContratos(contratos).renovacoes);

  if (!vigente)
    return <Muted>{t('accreditation.no_renewal_contract')}</Muted>;

  // BKT-1613: ciclo já encerrado não é chamado de "vigente"
  const vencido = cicloVencido(vigente);

  return (
    <div>
      <b>{t(vencido ? 'accreditation.renewal_contract_latest' : 'accreditation.renewal_contract_current')}:</b>{" "}
      {formateDate(vigente.inicioContrato)} {t('accreditation.period_to')} {formateDate(vigente.terminoContrato)}
      {vencido && <Warning>{t('accreditation.renewal_cycle_expired')}</Warning>}
      <Info>
        {t('accreditation.current_index_applied')}: <b>{vigente.indiceCorrecao || t('accreditation.no_index_applied')}</b>
      </Info>
    </div>
  );
}

// BKT-1508: Contrato Geral (datas sugeridas pela API, editáveis) + Contrato Renovação (somente leitura)
// sugerirAutomaticamente = false (unidade já cadastrada): sem Geral gravado, as datas não são preenchidas sozinhas —
// evita gravar um Geral "hoje" ao salvar outros dados de uma unidade legada (BKT-1514)
export default function ContratosEstabelecimento({ contratos, geralForm, setGeralForm, erroGeral, podeEditar, sugerirAutomaticamente = true }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [loadingSugestao, setLoadingSugestao] = React.useState(false);
  const [erroSugestao, setErroSugestao] = React.useState("");
  // Início digitado pelo usuário aguardando nova sugestão de término
  const [inicioParaSugestao, setInicioParaSugestao] = React.useState(null);
  const sugestaoInicialFeita = useRef(false);

  const { geral, renovacoes } = separarContratos(contratos);
  const vigente = renovacaoVigente(renovacoes);
  const temGeralGravado = geral !== null;

  // inicial = sugestão de Início e Término (só se o usuário ainda não digitou nada);
  // senão, só o Término e apenas se ele não foi editado manualmente nesse meio tempo
  const aplicarSugestao = useCallback((result, inicial) => {
    if (!result || result.status !== 200) {
      setErroSugestao(t('accreditation.contract_suggestion_error'));
      setGeralForm(prev => prev.sugestaoPendente ? { ...prev, sugestaoPendente: false } : prev);
      return;
    }
    setErroSugestao("");
    setGeralForm(prev => {
      if (inicial ? (prev.inicio !== "" || prev.termino !== "") : prev.terminoEditado)
        return prev.sugestaoPendente ? { ...prev, sugestaoPendente: false } : prev;
      return {
        ...prev,
        sugestaoPendente: false,
        inicio: inicial ? formateDate(result.data.inicioContrato) : prev.inicio,
        termino: formateDate(result.data.terminoContrato),
        terminoSugerido: true,
        terminoEditado: false,
      };
    });
  }, [setGeralForm, t]);

  // Unidade sem Contrato Geral: sugere Início (hoje) e Término assim que o usuário pode editar
  useEffect(() => {
    if (!podeEditar || !sugerirAutomaticamente || temGeralGravado || sugestaoInicialFeita.current)
      return;
    sugestaoInicialFeita.current = true;

    let ativo = true;
    let concluido = false;
    setLoadingSugestao(true);
    getSugestaoDatasContrato(TIPO_CONTRATO.GERAL, null).then(result => {
      concluido = true;
      if (!ativo)
        return;
      setLoadingSugestao(false);
      aplicarSugestao(result, true);
    });
    return () => {
      ativo = false;
      // requisição descartada antes de responder: libera nova tentativa
      if (!concluido) {
        sugestaoInicialFeita.current = false;
        setLoadingSugestao(false);
      }
    };
  }, [podeEditar, sugerirAutomaticamente, temGeralGravado, aplicarSugestao]);

  // Início alterado: nova sugestão de Término (sem sobrescrever um Término editado manualmente)
  useEffect(() => {
    if (!inicioParaSugestao)
      return;

    let ativo = true;
    const timer = setTimeout(() => {
      setLoadingSugestao(true);
      getSugestaoDatasContrato(TIPO_CONTRATO.GERAL, convertDate(inicioParaSugestao.valor)).then(result => {
        if (!ativo)
          return;
        setLoadingSugestao(false);
        aplicarSugestao(result, false);
      });
    }, DEBOUNCE_SUGESTAO_MS);

    return () => {
      ativo = false;
      clearTimeout(timer);
      setLoadingSugestao(false);
    };
  }, [inicioParaSugestao, aplicarSugestao]);

  const handleInicioChange = (event) => {
    const inicio = event.target.value;
    const aguardaSugestao = validateDate(inicio) && !geralForm.terminoEditado;
    setGeralForm(prev => ({ ...prev, inicio, sugestaoPendente: aguardaSugestao }));
    // objeto novo a cada digitação: a mesma data redigitada também dispara (e resolve) a sugestão pendente
    if (aguardaSugestao)
      setInicioParaSugestao({ valor: inicio });
  };

  const handleTerminoChange = (event) => {
    const termino = event.target.value;
    setGeralForm(prev => ({ ...prev, termino, terminoEditado: true, sugestaoPendente: false }));
  };

  const mensagemErro = (campo) => {
    if (!erroGeral || erroGeral.campo !== campo)
      return null;
    return (
      <FormHelperText className={classes.labelRootError}>
        <span>{erroGeral.mensagem}</span>
      </FormHelperText>
    );
  };

  return (
    <>
      <GridItem xs={12} sm={12} md={12}>
        <div className="subTitulo">
          <h6>{t('accreditation.general_contract')}</h6>
        </div>
      </GridItem>
      <GridItem xs={12} sm={6} md={3}>
        <FormControl fullWidth className={classes.formControl}>
          <TextField
            error={erroGeral && erroGeral.campo === "inicio" ? true : false}
            variant="outlined"
            label={t('accreditation.contract_start') + " *"}
            name="inicioContratoGeral"
            value={geralForm.inicio}
            onChange={handleInicioChange}
            disabled={!podeEditar}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputComponent: MaskDate }}
          />
          {mensagemErro("inicio")}
        </FormControl>
      </GridItem>
      <GridItem xs={12} sm={6} md={3}>
        <FormControl fullWidth className={classes.formControl}>
          <TextField
            error={erroGeral && erroGeral.campo === "termino" ? true : false}
            variant="outlined"
            label={t('accreditation.contract_end') + " *"}
            name="terminoContratoGeral"
            value={geralForm.termino}
            onChange={handleTerminoChange}
            disabled={!podeEditar}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: MaskDate,
              endAdornment: loadingSugestao ? (
                <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
              ) : null
            }}
          />
          {mensagemErro("termino")}
        </FormControl>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <Muted>{t('accreditation.general_contract_end_rule')}</Muted>
        {geralForm.terminoSugerido && !geralForm.terminoEditado &&
          <Info>{t('accreditation.general_contract_end_suggested')}</Info>}
        {geralForm.terminoSugerido && geralForm.terminoEditado &&
          <Warning>{t('accreditation.general_contract_end_edited')}</Warning>}
        {erroSugestao && <Warning>{erroSugestao}</Warning>}
        {!temGeralGravado && !sugerirAutomaticamente && !geralForm.inicio && !geralForm.termino &&
          <Warning>{t('accreditation.general_contract_pending_form')}</Warning>}
        {!podeEditar && <Warning>{t('accreditation.contract_permission_required')}</Warning>}
      </GridItem>

      <GridItem xs={12} sm={12} md={12}>
        <div className="subTitulo">
          <h6>{t('accreditation.renewal_contract')}</h6>
        </div>
        <Muted>{t('accreditation.renewal_contract_end_rule')}</Muted>
        {vigente &&
          <Info>
            {t('accreditation.current_index_applied')}: <b>{vigente.indiceCorrecao || t('accreditation.no_index_applied')}</b>
          </Info>}
      </GridItem>
      <GridItem xs={12} sm={12} md={12}>
        {renovacoes.length > 0 ? (
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t('accreditation.contract_start')}</th>
                <th>{t('accreditation.contract_end')}</th>
                <th>{t('accreditation.correction_index')}</th>
                <th>{t('label.status')}</th>
              </tr>
            </thead>
            <tbody>
              {renovacoes.map((ciclo, index) => (
                <tr key={ciclo.estabelecimentoContratoId || index} style={ciclo === vigente ? { fontWeight: 'bold' } : null}>
                  <td>{formateDate(ciclo.inicioContrato)}</td>
                  <td>{formateDate(ciclo.terminoContrato)}</td>
                  <td>{ciclo.indiceCorrecao}</td>
                  <td>{ciclo === vigente
                    ? t(cicloVencido(ciclo) ? 'accreditation.renewal_cycle_expired' : 'accreditation.renewal_cycle_current')
                    : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="table table-borderless">
            <tbody>
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  {temGeralGravado ? t('information_message.no_records_found') : t('accreditation.renewal_contract_generated_on_save')}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </GridItem>
    </>
  );
}

